import React, { useEffect, useState } from 'react'
import { InView } from 'react-intersection-observer'
import { Button, LinkButton } from '@/atoms/Button'
import { CircleInfoIcon } from '@/atoms/Icons/CircleInfoIcon'
import { LoadingSpinnerIcon } from '@/atoms/Icons/LoadingSpinnerIcon'
import { CaptionSM, ParagraphSM, TitleLG } from '@/atoms/Text'
import { useShopifyCart } from '@/services/Shopify/hooks'
import { useSafeTrack } from '@/utils/analytics'
import { useEcommerceEvents } from '@/utils/analytics/ecommerce-events'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { logger } from '@/utils/logging'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { useShopifyUserAttributes } from '@/views/PreSales/hooks/useShopifyUserAttributes'
import { GroupTicketsModalTypes } from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/GroupTicketsModal/GroupTicketsModal'
import { TicketOption } from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/GroupTicketsModal/TicketOption'

const GROUP_DISCOUNT_RATE = 0.5
const OPTIONS = [10, 20, 50, 100, 200]

interface Props {
  setModalType: React.Dispatch<React.SetStateAction<GroupTicketsModalTypes>>
  ticketPrice: number
  theatricalSlug: string
  groupTicketProductVariantId: string
}

export const ShopifyGroupTickets: React.FC<Props> = ({
  setModalType,
  ticketPrice,
  groupTicketProductVariantId,
  theatricalSlug,
}) => {
  const { t } = useTranslate('tickets')
  const track = useSafeTrack()
  const { query } = useSafeRouter()
  const { trackProductAdded, trackProductViewed } = useEcommerceEvents()
  const [selectedQuantity, setSelectedQuantity] = useState(OPTIONS[3] ?? 0)
  const [isProcessing, setIsProcessing] = useState(false)
  const { createCart, applyDiscountCode } = useShopifyCart()
  const [shopifyCheckoutUrl, setShopifyCheckoutUrl] = useState('')
  const attributes = useShopifyUserAttributes('theatrical-group-tickets', 'theatrical group tickets', theatricalSlug)

  useEffect(() => {
    if (!selectedQuantity) return

    setIsProcessing(true)
    const createShopifyCheckoutUrl = async () => {
      try {
        const { cart } = await createCart({
          lines: [
            {
              quantity: selectedQuantity,
              merchandiseId: `gid://shopify/ProductVariant/${groupTicketProductVariantId}`,
            },
          ],
          attributes,
        })

        if (!cart?.id) return null

        const discountCodeParam = getItemOrFirstEntry(query?.groupDiscountCode)
        const discountCode = discountCodeParam ? discountCodeParam : 'GROUPTICKETSDISCOUNT'

        const finalCart = await applyDiscountCode({ cartId: cart.id, discountCodes: [discountCode] })

        if (!finalCart?.cart?.checkoutUrl) return

        setShopifyCheckoutUrl(finalCart?.cart?.checkoutUrl)
      } catch (err) {
        logger().error('Failed to create shopifyCheckoutUrl for group tickets', err)
      } finally {
        setIsProcessing(false)
      }
    }

    createShopifyCheckoutUrl()
  }, [
    applyDiscountCode,
    attributes,
    createCart,
    groupTicketProductVariantId,
    query?.groupDiscountCode,
    selectedQuantity,
  ])

  useEffect(() => {
    track('Group Tickets Options Viewed')
  }, [track])

  const handleCtaClick = async (quantity: number) => {
    try {
      track('Group Tickets Checkout Started')

      trackProductAdded({
        category: 'group tickets',
        funnel: 'theatrical',
        name: `Group Ticket ${theatricalSlug}`,
        price: Number((ticketPrice * GROUP_DISCOUNT_RATE).toFixed(2)),
        currency: 'USD',
        quantity: quantity,
        product_id: `group-tickets-${theatricalSlug}`,
        sku: `group-tickets-${theatricalSlug}`,
        projectSlug: theatricalSlug,
      })
    } catch (err) {
      logger().error('Failed to create group ticket checkout', err)
    }
  }

  return (
    <InView
      className="flex flex-col gap-6"
      triggerOnce
      onChange={(inView) => {
        if (!inView) return

        trackProductViewed({
          category: 'group tickets',
          funnel: 'theatrical',
          name: `Group Ticket ${theatricalSlug}`,
          price: Number((ticketPrice * GROUP_DISCOUNT_RATE).toFixed(2)),
          currency: 'USD',
          quantity: selectedQuantity,
          product_id: `group-tickets-${theatricalSlug}`,
          sku: `group-tickets-${theatricalSlug}`,
          url: '',
          projectSlug: theatricalSlug,
          value: selectedQuantity * ticketPrice * GROUP_DISCOUNT_RATE,
        })
      }}
    >
      <div className="flex flex-col gap-2 text-center">
        <TitleLG weight="bold">{t('flexGroupTickets', 'Flex Group Tickets')}</TitleLG>

        <ParagraphSM>
          <Translate t={t} i18nKey="ticketPricesEstimatedAt15AdditionalChargesMayApply">
            Ticket prices estimated at $15. Additional charges may apply.{' '}
            <Button onClick={() => setModalType('faq')} className="inline font-bold underline" variant="plain">
              Learn More
            </Button>
          </Translate>
        </ParagraphSM>

        <div className="flex gap-1">
          <CircleInfoIcon size={12} color="core-warning-800" className="shrink-0" />
          <CaptionSM color="core-warning-800" className="text-left">
            {t(
              'theatersMayLimitSeatsPerOrderV2',
              'Theaters may limit seats per order. If you hit the max, complete your reservation and add more after checkout.',
            )}
          </CaptionSM>
        </div>
      </div>

      <div className="flex w-full flex-col gap-2">
        {groupTicketProductVariantId &&
          OPTIONS.map((quantity) => {
            const title = t('groupTicketTitle', '{{quantity}} Tickets', { quantity })

            const subTitle = t(
              'groupTicketDiscountExperimentSubtitlev2',
              'Buy {{paidQuantity}} → Get {{freeQuantity}} Free',
              {
                paidQuantity: quantity / 2,
                freeQuantity: quantity / 2,
              },
            )

            const isSelected = selectedQuantity === quantity
            return (
              <TicketOption
                key={quantity}
                onClick={() => setSelectedQuantity(quantity)}
                title={title}
                subTitle={subTitle}
                ticketPrice={ticketPrice}
                ticketQuantity={quantity}
                selected={isSelected}
              />
            )
          })}
      </div>

      {shopifyCheckoutUrl ? (
        <LinkButton
          external
          href={shopifyCheckoutUrl}
          onClick={() => handleCtaClick(selectedQuantity)}
          className="mx-auto flex w-full max-w-[380px] items-center justify-center gap-1 md:w-fit"
          variant="black"
          disabled={isProcessing}
        >
          {isProcessing && <LoadingSpinnerIcon color="black" size={16} />}
          {t('checkout', 'Checkout')}
        </LinkButton>
      ) : (
        <LoadingSpinnerIcon size={32} color="black" className="mx-auto" />
      )}
    </InView>
  )
}
